/**
 * MemoriesPage — the family keepsake book.
 *
 * Residence Journal · not a photo gallery. A leather scrapbook of the
 * small moments a family wants to keep.
 *
 * Growing home: every shelf ships empty and invites the resident to
 * place the first keepsake.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

const SHELVES = [
  { id: "this-month",       label: "This month",              subtitle: "Small things worth remembering.",                             tag: "this-month" },
  { id: "family-holidays",  label: "Family holidays",          subtitle: "Trips, weekends, long summer evenings.",                      tag: "holiday" },
  { id: "birthdays",        label: "Birthdays",                subtitle: "Cakes, candles and grinning children.",                       tag: "birthday" },
  { id: "school-moments",   label: "School moments",           subtitle: "First days, awards, plays, hard-won maths.",                  tag: "school" },
  { id: "funny",            label: "Funny things we want to remember", subtitle: "Overheards, silliness, kitchen chaos.",                tag: "funny" },
  { id: "videos",           label: "Videos",                   subtitle: "Moving pictures, quiet clips, shaky little films.",           tag: "video" },
  { id: "voice-notes",      label: "Voice notes",              subtitle: "Voices a family wants to hear again.",                        tag: "voice" },
  { id: "letters",          label: "Letters",                  subtitle: "Cards, notes, keepsake handwriting.",                          tag: "letter" },
  { id: "childrens-artwork",label: "Children's artwork",       subtitle: "Fridge-door masterpieces that deserve keeping.",              tag: "artwork" },
  { id: "milestones",       label: "Milestones",               subtitle: "First steps, first words, first haircuts.",                    tag: "milestone" },
];

export default function MemoriesPage() {
  const { phase } = useLightPhase();
  useHomeOSState();
  const [memories, setMemories] = usePersistentState("keepsakes.memories", []);
  const [openShelfId, setOpenShelfId] = useState(null);
  const [adding, setAdding] = useState(null);

  const shelfCounts = useMemo(() => {
    const m = {};
    SHELVES.forEach(s => { m[s.id] = memories.filter(x => x.shelfId === s.id).length; });
    return m;
  }, [memories]);

  const openShelf = openShelfId ? SHELVES.find(s => s.id === openShelfId) : null;
  const shelfMemories = openShelf ? memories.filter(m => m.shelfId === openShelf.id) : [];

  const totalCount = memories.length;
  const focal = totalCount === 0
    ? "The keepsake shelves are empty. Add the first small thing worth remembering."
    : totalCount === 1
      ? "One keepsake, gently stored."
      : `${totalCount} keepsakes, gently stored.`;

  const openAdd = (shelfId = "this-month") => {
    setAdding({ id: `m_${Date.now()}`, shelfId, title: "", note: "", when: new Date().toISOString().slice(0, 10) });
  };
  const closeAdd = () => setAdding(null);

  const saveMemory = () => {
    if (!adding) return;
    if (!adding.title.trim()) { toast.error("Give this keepsake a title."); return; }
    setMemories(list => [{ ...adding, title: adding.title.trim(), note: adding.note.trim() }, ...list]);
    toast.success("Added to the keepsake book.");
    closeAdd();
  };
  const removeMemory = (id) => {
    if (typeof window !== "undefined" && !window.confirm("Remove this keepsake?")) return;
    setMemories(list => list.filter(m => m.id !== id));
    toast("Removed.");
  };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="family" />} phase={phase} testid="module-page-memories">
      <PageHeading
        testid="memories-hero"
        module="family"
        room="Keepsake book"
        title="The Keepsake Book"
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={() => openAdd("this-month")} testid="memories-add-btn">
              Add a keepsake
            </PillButton>
            <TextLink onClick={() => window.location.href = "/home-os/family"} testid="memories-view-family">
              View The Family Room
            </TextLink>
          </>
        }
      />
      <div className="hidden" data-testid="module-title" aria-hidden>Keepsake book</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Small moments the family wants to keep.</div>

      <EditorialSection
        testid="memories-shelves"
        moduleMark={{ module: 'family', label: openShelf ? openShelf.label : 'Shelves' }}
        title={openShelf ? openShelf.label : "Every family has shelves worth building"}
        aside={
          openShelf ? (
            <TextLink onClick={() => setOpenShelfId(null)} testid="memories-back">
              ← All shelves
            </TextLink>
          ) : (
            <TextLink onClick={() => openAdd("this-month")} testid="memories-add-inline">
              Add a keepsake
            </TextLink>
          )
        }
      >
        {!openShelf ? (
          <ul>
            {SHELVES.map((s, i, arr) => {
              const count = shelfCounts[s.id] || 0;
              return (
                <li
                  key={s.id}
                  className={`${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenShelfId(s.id)}
                    className="w-full grid grid-cols-[1fr_auto_auto] items-baseline gap-6 py-4 text-left transition-ara hover:opacity-70"
                    data-testid={`memories-shelf-${s.id}`}
                  >
                    <div className="min-w-0">
                      <p
                        className="text-[15.5px] leading-tight"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                      >
                        {s.label}
                      </p>
                      <p
                        className="mt-1 text-[13px]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                      >
                        {s.subtitle}
                      </p>
                    </div>
                    <p
                      className="text-[13px] tabular-nums whitespace-nowrap"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                      data-testid={`memories-shelf-count-${s.id}`}
                    >
                      {count} {count === 1 ? 'keepsake' : 'keepsakes'}
                    </p>
                    <span
                      className="text-[13px]"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)' }}
                      aria-hidden
                    >
                      →
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : shelfMemories.length === 0 ? (
          <div>
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{
                fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '38ch',
              }}
              data-testid={`memories-empty-${openShelf.id}`}
            >
              This shelf is waiting. Add the first {openShelf.label.toLowerCase()}.
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => openAdd(openShelf.id)}
                testid={`memories-add-first-${openShelf.id}`}
              >
                Place the first
              </PillButton>
            </div>
          </div>
        ) : (
          <ul data-testid={`memories-list-${openShelf.id}`}>
            {shelfMemories.map((m, i, arr) => (
              <li
                key={m.id}
                className={`grid grid-cols-[110px_1fr_auto] items-baseline gap-4 py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`memories-item-${m.id}`}
              >
                <p
                  className="caption-ara"
                  style={{ color: 'var(--ara-ink-muted)' }}
                >
                  {new Date(m.when).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <div className="min-w-0">
                  <p
                    className="text-[15px] leading-tight"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                  >
                    {m.title}
                  </p>
                  {m.note && (
                    <p
                      className="mt-1 italic text-[13.5px] leading-[1.5]"
                      style={{
                        fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                        color: 'var(--ara-ink-muted)', fontWeight: 400,
                        maxWidth: '52ch',
                      }}
                    >
                      {m.note}
                    </p>
                  )}
                </div>
                <TextLink onClick={() => removeMemory(m.id)} testid={`memories-remove-${m.id}`}>
                  Remove
                </TextLink>
              </li>
            ))}
          </ul>
        )}
      </EditorialSection>

      <EditorialSection
        testid="memories-ara-helps"
        moduleMark={{ module: 'family', label: 'Ara' }}
        title="How Ara helps"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch',
          }}
        >
          Ara can gather the small things — voice notes, funny lines, school moments — and quietly file them onto the right shelf so the family keeps them without effort.
        </p>
      </EditorialSection>

      <Colophon>ARA · Keepsake</Colophon>

      {adding && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(31, 31, 31, 0.5)' }}
          onClick={closeAdd}
          data-testid="memories-add-dialog"
        >
          <div
            className="w-full max-w-md rounded-[20px] p-6 sm:p-8"
            style={{ background: 'var(--ara-surface)', border: '1px solid var(--ara-line)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="caption-ara mb-2">New keepsake</p>
            <h3
              className="text-[24px] leading-tight tracking-[-0.01em]"
              style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              A small thing worth remembering
            </h3>
            <div className="mt-6 space-y-4">
              <div>
                <label className="caption-ara block mb-1">Title</label>
                <input
                  type="text"
                  value={adding.title}
                  onChange={(e) => setAdding({ ...adding, title: e.target.value })}
                  placeholder="A short line"
                  className="w-full bg-transparent outline-none py-2 text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderBottom: '1px solid var(--ara-line)' }}
                  data-testid="memories-add-title"
                />
              </div>
              <div>
                <label className="caption-ara block mb-1">Shelf</label>
                <select
                  value={adding.shelfId}
                  onChange={(e) => setAdding({ ...adding, shelfId: e.target.value })}
                  className="w-full bg-transparent outline-none py-2 text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderBottom: '1px solid var(--ara-line)' }}
                  data-testid="memories-add-shelf"
                >
                  {SHELVES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="caption-ara block mb-1">When</label>
                <input
                  type="date"
                  value={adding.when}
                  onChange={(e) => setAdding({ ...adding, when: e.target.value })}
                  className="w-full bg-transparent outline-none py-2 text-[14px]"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', borderBottom: '1px solid var(--ara-line)' }}
                  data-testid="memories-add-when"
                />
              </div>
              <div>
                <label className="caption-ara block mb-1">Note</label>
                <textarea
                  value={adding.note}
                  onChange={(e) => setAdding({ ...adding, note: e.target.value })}
                  rows={3}
                  placeholder="A sentence, a voice, a line worth keeping…"
                  className="w-full rounded-lg px-3 py-2 outline-none resize-none"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--ara-line)',
                    color: 'var(--ara-ink)',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '13.5px',
                  }}
                  data-testid="memories-add-note"
                />
              </div>
            </div>
            <div className="mt-7 flex items-center justify-end gap-6">
              <button
                type="button"
                onClick={closeAdd}
                className="link-ara"
                data-testid="memories-add-cancel"
              >
                Cancel
              </button>
              <PillButton variant="solid" onClick={saveMemory} testid="memories-add-save">
                Save
              </PillButton>
            </div>
          </div>
        </div>
      )}

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
